import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { verificationService } from '../services/verificationService';
import { StepNames, DocumentStatus } from '../types';
const INITIAL_STATE = {
    currentStep: StepNames.ENTRY,
    isLoading: false,
    error: null,
    userData: {},
    verificationData: {},
};
const POLLING_INTERVAL = 10000;
export const useVerificationFlow = () => {
    const [state, setState] = useState(INITIAL_STATE);
    const pollingIntervalRef = useRef(null);
    const updateState = useCallback((updates) => {
        setState((prev) => ({ ...prev, ...updates }));
    }, []);
    const getUrlParams = useCallback(() => {
        const urlParams = new URLSearchParams(window.location.search);
        const tokenSource = urlParams.get('tokenSource');
        const flow = urlParams.get('flow') || undefined;
        return { tokenSource, flow };
    }, []);
    const checkVerificationStatus = useCallback((scenarios, initialCheck = false) => {
        if (!scenarios.length) {
            return StepNames.ERROR;
        }
        const allRequired = scenarios.every((scenario) => scenario.status === DocumentStatus.REQUIRED || scenario.status === DocumentStatus.PENDING);
        if (initialCheck && allRequired) {
            return StepNames.ENTRY;
        }
        const someRejected = scenarios.some((scenario) => scenario.status === DocumentStatus.REJECTED);
        if (someRejected) {
            return StepNames.REJECTED;
        }
        const allVerified = scenarios.every((scenario) => scenario.status === DocumentStatus.VERIFIED);
        if (allVerified) {
            return StepNames.SUCCESS;
        }
        return StepNames.VERIFY_IDENTITY;
    }, []);
    const stopPolling = useCallback(() => {
        if (pollingIntervalRef.current) {
            clearTimeout(pollingIntervalRef.current);
            pollingIntervalRef.current = null;
        }
    }, []);
    const fetchSummaryData = useCallback(async (initialCheck = false, isPolling = false) => {
        try {
            if (!isPolling) {
                updateState({ isLoading: true, error: null });
            }
            const { tokenSource, flow } = getUrlParams();
            const data = await verificationService.fetchSummary(tokenSource, flow);
            const nextStep = checkVerificationStatus(data.scenarios, initialCheck);
            updateState({
                currentStep: nextStep,
                userData: {
                    firstName: data.firstName,
                    scenarios: data.scenarios,
                },
                isLoading: false,
            });
            const hasSubmitted = data.scenarios.some((scenario) => scenario.status === DocumentStatus.SUBMITTED);
            if (hasSubmitted) {
                pollingIntervalRef.current = window.setTimeout(() => {
                    fetchSummaryData(false, true);
                }, POLLING_INTERVAL);
            }
            else {
                stopPolling();
            }
        }
        catch (error) {
            const apiError = error;
            console.error('Failed to fetch summary:', apiError);
            updateState({
                isLoading: false,
                error: apiError.code || 'FETCH_ERROR',
                currentStep: StepNames.ERROR,
            });
            stopPolling();
        }
    }, [checkVerificationStatus, updateState, getUrlParams, stopPolling]);
    const selectDocumentType = useCallback((scenarioType) => {
        updateState({
            currentStep: StepNames.DOCUMENT_SELECTION,
            verificationData: { scenarioType },
        });
    }, [updateState]);
    const handlePostMessage = useCallback(async (event) => {
        if (!event.data) {
            return;
        }
        try {
            const { payload, transactionReference } = JSON.parse(event.data);
            const { tokenSource } = getUrlParams();
            const payloadValue = payload && payload.value ? payload.value.toLowerCase() : '';
            if (payloadValue === 'success') {
                await verificationService.confirmUpload({ tokenSource, transactionReference });
                window.removeEventListener('message', handlePostMessage);
                await fetchSummaryData();
            }
            else if (payloadValue === 'error') {
                window.removeEventListener('message', handlePostMessage);
                await fetchSummaryData();
            }
        }
        catch (error) {
            console.error('PostMessage handling error:', error);
        }
    }, [fetchSummaryData]);
    const startVerification = useCallback(async (scenarioType, documentType) => {
        try {
            const { tokenSource } = getUrlParams();
            updateState({ isLoading: true, error: null });
            const data = await verificationService.initCapture({
                scenarioType,
                documentType,
                tokenSource,
            });
            updateState({
                currentStep: StepNames.VERIFICATION_PROCESS,
                verificationData: {
                    ...state.verificationData,
                    redirectUrl: data.redirectUrl,
                    transactionReference: data.transactionReference,
                },
                isLoading: false,
            });
            window.addEventListener('message', handlePostMessage);
        }
        catch (error) {
            const apiError = error;
            console.error('Failed to initialize capture:', apiError);
            updateState({
                isLoading: false,
                error: apiError.code || 'INIT_CAPTURE_ERROR',
                currentStep: StepNames.ERROR,
            });
        }
    }, [state.verificationData, updateState, handlePostMessage, fetchSummaryData]);
    const goToVerification = useCallback(() => {
        updateState({ currentStep: StepNames.VERIFY_IDENTITY });
    }, [updateState]);
    const reset = useCallback(async () => {
        await fetchSummaryData(true);
        stopPolling();
        window.removeEventListener('message', handlePostMessage);
    }, [stopPolling]);
    useEffect(() => {
        return () => {
            stopPolling();
            window.removeEventListener('message', handlePostMessage);
        };
    }, [handlePostMessage, stopPolling]);
    const isJumioMock = useMemo(() => {
        if (!state.verificationData.redirectUrl) {
            return false;
        }
        return state.verificationData.redirectUrl.includes('jumio-mock');
    }, [state.verificationData.redirectUrl]);
    return {
        state,
        isJumioMock,
        actions: {
            fetchSummaryData,
            selectDocumentType,
            startVerification,
            goToVerification,
            reset,
        },
    };
};
//# sourceMappingURL=useVerificationFlow.js.map